import React, { useEffect, useState } from 'react';
import {
  Dropdown,
  DropdownOverlay,
  AutoComplete,
  ActionList,
  ActionListItem,
  Box,
  Text,
  Spinner,
} from '@razorpay/blade/components';
import { RegisterGenericType } from 'forms/useRegister';
import api from 'api';
import { useQuery } from 'react-query';
import { getStateName } from 'components/EmployeeOnboarding/EmployeeSelfOnboarding/utils';
import { officeLocationQueryKeys } from 'components/OfficeLocation/PreviewOfficeLocations/data';

type SelectOfficeLocationProps = {
  formData: Partial<{ officeLocation: string[] }>;
  registerFormData: RegisterGenericType<{ officeLocation: string[] }>;
};

export const SelectOfficeLocationV2 = ({
  registerFormData,
  formData,
}: SelectOfficeLocationProps) => {
  const [searchValue, setSearchValue] = useState<string>();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [officeLocationQueryKeys.GET_OFFICE_LOCATIONS],
    queryFn: () =>
      api.officeLocation.fetchOfficeLocations({
        page: 1,
        limit: 100,
      }),
  });

  const officeLocations = data?.data || [];

  const selectedOfficeLocation = formData.officeLocation?.[0];

  const selectedOfficeLocationName = officeLocations?.find(
    ({ id }) => id === Number(selectedOfficeLocation),
  )?.office_name;

  const filteredOfficeLocations =
    officeLocations?.filter(({ office_name: name }) =>
      searchValue ? name.toLowerCase().includes(searchValue.toLowerCase()) : true,
    ) || [];

  useEffect(() => {
    if (selectedOfficeLocationName) {
      setSearchValue(selectedOfficeLocationName);
    }
  }, [selectedOfficeLocationName]);

  return (
    <Dropdown
      selectionType="single"
      onOpenChange={(isOpen) => {
        if (!isOpen && selectedOfficeLocation) {
          setSearchValue(selectedOfficeLocationName);
        } else {
          setSearchValue('');
        }
      }}>
      <AutoComplete
        label="Office Location"
        placeholder="Select"
        necessityIndicator="required"
        isRequired
        inputValue={searchValue || ''}
        onInputValueChange={({ value }) => {
          setSearchValue(value || '');
        }}
        filteredValues={filteredOfficeLocations.map(({ id }) => id.toString())}
        {...registerFormData('officeLocation', 'select')}
      />
      <DropdownOverlay>
        {isLoading || isFetching ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="spacing.5"
            padding="spacing.5">
            <Spinner accessibilityLabel="loading states" />
          </Box>
        ) : filteredOfficeLocations?.length ? (
          <ActionList>
            {filteredOfficeLocations?.map((officeLocation) => (
              <ActionListItem
                key={officeLocation.id}
                title={officeLocation.office_name}
                value={officeLocation.id.toString()}
                isSelected={selectedOfficeLocation === officeLocation.id.toString()}
                description={getStateName(officeLocation.state)}
              />
            ))}
          </ActionList>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="spacing.5"
            padding="spacing.5">
            <Box display="flex" flexDirection="column" gap="spacing.3">
              <Text size="medium" weight="semibold" textAlign="center">
                No Office Locations Found
              </Text>
            </Box>
          </Box>
        )}
      </DropdownOverlay>
    </Dropdown>
  );
};
